import { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Icon } from '@stax-core/truffle-components';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '../Button';
import { VerifyIdentityProps } from './VerifyIdentity.types';

// IMPORTANT: icons other than the ones in truffle's setupIcons.ts must be
// registered with the FontAwesome library before they can be rendered.
library.add(faCheckCircle);

const SUCCESS_BG = '#ebf9f0'; // Green/50
const SUCCESS_GREEN = '#28cb35'; // Green/450
const NEUTRAL_BG = '#f2f2f2'; // Neutral/50
const ERROR_RED = '#d70101';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 421px;
  max-width: 100%;
  margin-top: 1rem;

  && button {
    margin: 0;
  }
`;

const Row = styled.div<{ $success?: boolean; $error?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 8px 16px;
  border-radius: 4px;
  background: ${props => (props.$success ? SUCCESS_BG : NEUTRAL_BG)};
  border: 1px solid ${props => (props.$error ? ERROR_RED : 'transparent')};
`;

const SuccessLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-family: 'Roboto', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.5rem;
`;

const RequiredLabel = styled.span`
  display: flex;
  align-items: center;
  font-family: 'Roboto', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.5rem;
  white-space: nowrap;
`;

const CheckIcon = styled(Icon)`
  color: ${SUCCESS_GREEN};
`;

const RequiredText = styled.span`
  font-size: 0.75rem;
  color: ${ERROR_RED};
`;

const VerifyButton = styled(Button)`
  min-width: 121px;
  margin: 0;
  height: auto;
  padding: 6px 16px;
  border-radius: 2px;
  background: #b93be4;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;

  &:hover,
  &:focus {
    background: #b93be4;
    color: white;
  }

  @media (max-width: 1000px) {
    border-radius: 2px;
  }
`;

/**
 * "Verify Identity" prompt on the review page (SPX-2112).
 *
 * Renders one of three states driven by `status`:
 * - `complete` → green "Identity check complete" row
 * - `pending`  → "Identity verification required" row with a Verify CTA
 *   (plus a red outline and "Required" label when `hasError`)
 * - `null`     → nothing (step-up not required)
 */
export const VerifyIdentity: FunctionComponent<VerifyIdentityProps> = ({
  status,
  hasError = false,
  onVerify,
  className,
}) => {
  if (status === null) return null;

  if (status === 'complete') {
    return (
      <Wrapper className={className}>
        <Row $success data-testid="verify-identity-complete">
          <SuccessLabel>
            <CheckIcon icon={['fas', 'check-circle']} />
            Identity check complete
          </SuccessLabel>
        </Row>
      </Wrapper>
    );
  }

  return (
    <Wrapper className={className}>
      <Row $error={hasError} data-testid="verify-identity-required">
        <RequiredLabel>Identity verification required</RequiredLabel>
        <VerifyButton
          type="button"
          variant="solid"
          motif="primary"
          size="small"
          border="rounded"
          onClick={onVerify}
          id="verify-identity-button"
          data-testid="verify-identity-button"
        >
          Verify identity
        </VerifyButton>
      </Row>
      {hasError && (
        <RequiredText role="alert" data-testid="verify-identity-error">
          Required
        </RequiredText>
      )}
    </Wrapper>
  );
};
